// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import getOrderStatusMappingStatusColor from "@store/Framework/Common/Utilities/getOrderStatusMappingStatusColor";
import { orderStatusColoredStyles } from "@store/Themes/common/mixins/order";
// import { tableGetEllipsisCellProps, tableProps } from "@store/Themes/common/props/table"; // Store Custom Code
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import HistoryContext, { useHistory } from "@insite/mobius/utilities/HistoryContext";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { tableGetEllipsisCellProps, tableProps } from "@store/Themes/common/props/table";
import constants from "@store/constants";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LinkTo from "@store/Library/Components/LinkTo";
import StatusBadge from "@store/Library/Components/StatusBadge";
import { orderStatusColorMap } from "@store/Library/Components/statusColorMaps";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    // Store Custom Code
    orderHistoryPageLink: getPageLinkByPageType(state, constants.pageType.orderHistory),
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
    isReordering: state.pages.orderHistory.isReordering,
    isUpdatingCart: state.context.isUpdatingCart,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
    addToCartPopupTimeout: getSettingsCollection(state).cartSettings.addToCartPopupTimeout,
    orderStatusMappingDataView: getOrderStatusMappingDataView(state), // Starter Custom Code
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // Store Custom Code
    showOrderNumber?: boolean;
    showDate?: boolean;
    showOrderTotal?: boolean;
    showStatus?: boolean;
    showShipTo?: boolean;
    showPONumber?: boolean;
    showRequestedDeliveryDate?: boolean;
    showReorderProducts?: boolean;
    showWarehouse?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // Starter Custom Code
    orderNumberCell?: DataTableCellProps;
    dateCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    totalCell?: DataTableCellProps;
    // Store Custom Code
    reorderCell?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    warehouseCell?: DataTableCellProps;
    customerPOLink?: LinkPresentationProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    shipToCell: tableProps.shipToCell, // Store Custom Code
    customerPOCell: tableGetEllipsisCellProps(95),
    statusCell: tableGetEllipsisCellProps(95),
    totalCell: tableProps.amountCell,
    /* Store Custom Code - Start */
    reorderCell: tableProps.tightCell,
    reorderButton: tableProps.button,
    /* Store Custom Code - End */
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    // Starter Custom Code
    orderStatusMappingDataView,
    // Store Custom Code
    isVmiOrderHistoryPage,
    isReordering,
    isUpdatingCart,
    showOrderNumber = true,
    showDate = true,
    showOrderTotal = true,
    showStatus = true,
    showShipTo = true,
    showPONumber = true,
    showWarehouse = true,
    showRequestedDeliveryDate = true,
    showReorderProducts = true,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    orderHistoryPageLink,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));
    /* Store Custom Code - Start */
    const dispatch = useAppDispatch();
    const toaster = useToaster();
    const history = useHistory();
    /* Store Custom Code - End */

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    /* Starter Custom Code - Start */
    const statusColor = React.useMemo(
        () => getOrderStatusMappingStatusColor(order, orderStatusMappingDataView.value),
        [order, orderStatusMappingDataView.value],
    );

    const statusCellCss = React.useMemo(() => {
        if (!statusColor) {
            return styles.statusCell;
        }

        return mergeProps<OrderSummaryCardStyles["statusCell"]>(styles.statusCell, {
            css: orderStatusColoredStyles(statusColor),
        });
    }, [styles.statusCell, statusColor]);

    const orderNumber = orderSettings.showWebOrderNumber
        ? order.webOrderNumber || order.erpOrderNumber
        : order.erpOrderNumber || order.webOrderNumber;

    const shipTo = `${order.shipToCity} ${order.shipToPostalCode}`; // Store Custom Code

    /* Store Custom Code - Start */
    const reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) =>
        dispatch(
            reorder({
                orderNumber,
                onSuccess: () => onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder),
                onError: message => onReorderError(message),
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.();
                    }
                },
            }),
        );

    const onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!showAddToCartConfirmationDialog) {
            return;
        }
        toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: addToCartPopupTimeout,
        });
    };

    const onReorderError = (message: string) => {
        toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: addToCartPopupTimeout,
        });
    };
    /* Store Custom Code - End */

    return (
        <DataTableRow key={order.id}>
            {/* Store Custom Code - Start */}
            {showOrderNumber && (
                <DataTableCell {...styles.orderNumberCell} data-test-selector="orderHistoryTable_tableCell_orderNumber">
                    {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
                </DataTableCell>
            )}
            {showPONumber && (
                <DataTableCell {...styles.customerPOCell}>
                    <LinkTo
                        {...styles.customerPOLink}
                        title={order.customerPO}
                        pageLink={orderHistoryPageLink}
                        pageIdSegment={`?customerSequence=-1&poNumber=${order.customerPO}`}
                    />
                </DataTableCell>
            )}
            {showDate && (
                <DataTableCell {...styles.dateCell} data-test-selector="orderHistoryTable_tableCell_date">
                    {orderDateDisplay}
                </DataTableCell>
            )}
            {showRequestedDeliveryDate && (
                <DataTableCell
                    {...styles.dateCell}
                    data-test-selector="orderHistoryTable_tableCell_requestedDeliveryDateDisplay"
                >
                    {order.requestedDeliveryDateDisplay
                        ? getLocalizedDateTime({
                              dateTime: new Date(order.requestedDeliveryDateDisplay),
                              language,
                              options: {
                                  year: "numeric",
                                  month: "numeric",
                                  day: "numeric",
                              },
                          })
                        : null}
                </DataTableCell>
            )}
            {showShipTo && <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>}
            {showOrderTotal && <DataTableCell {...styles.totalCell}>{order.orderTotalDisplay}</DataTableCell>}
            {showStatus && (
                <DataTableCell {...styles.statusCell} data-test-selector="orderHistoryTable_tableCell_status">
                    {order.statusDisplay ? (
                        <StatusBadge
                            status={order.status}
                            displayText={order.statusDisplay}
                            colorMap={orderStatusColorMap}
                        />
                    ) : (
                        translate("--")
                    )}
                </DataTableCell>
            )}
            {showReorderProducts && !isVmiOrderHistoryPage && (
                <DataTableCell {...styles.reorderCell}>
                    <Button
                        disabled={!order.canAddToCart || Object.keys(isReordering).length > 0 || isUpdatingCart}
                        {...styles.reorderButton}
                        onClick={() => reorderClick(orderNumber, orderNumber, !!order.vmiLocationId)}
                        data-test-selector="orderHistoryTable_Reorder"
                        isLoading={Boolean(isReordering[orderNumber])}
                    >
                        {translate("Reorder")}
                    </Button>
                </DataTableCell>
            )}
            {showWarehouse && <DataTableCell {...styles.warehouseCell}>{order.properties?.warehouse}</DataTableCell>}
            {/* Store Custom Code - End */}
        </DataTableRow>
    );
    /* Starter Custom Code - End */
};

export default connect(mapStateToProps)(OrderSummaryCard);
